import axios from "axios";
import { toSafeMessage } from "@/lib/safeMessage";

const isBrowser = typeof window !== "undefined";
const currentOrigin = isBrowser ? window.location.origin.replace(/\/+$/, "") : "";
const currentHost = isBrowser ? String(window.location.hostname || "").toLowerCase() : "";
const envBackend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const productionBackendByHost =
  currentHost === "app.obihub.app" || currentHost === "admin.obihub.app"
    ? "https://api.obihub.app"
    : "";
const isProductionHost = Boolean(productionBackendByHost);

function isPrivateOrLocalHost(hostname) {
  const host = String(hostname || "").toLowerCase();
  if (!host) return false;
  if (host === "localhost" || host === "::1" || host === "[::1]") return true;
  if (host.startsWith("127.")) return true;
  if (host.startsWith("10.")) return true;
  if (host.startsWith("192.168.")) return true;
  if (host.startsWith("172.")) {
    const second = Number(host.split(".")[1] || -1);
    if (second >= 16 && second <= 31) return true;
  }
  return false;
}

function sanitizeEnvBackend(url) {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    if (
      isPrivateOrLocalHost(parsed.hostname) &&
      !isPrivateOrLocalHost(currentHost)
    ) {
      return "";
    }
    if (
      isPrivateOrLocalHost(currentHost) &&
      (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1" || parsed.hostname === "::1")
    ) {
      return "";
    }
    if (isProductionHost) {
      if (parsed.protocol !== "https:") return "";
      if (isPrivateOrLocalHost(parsed.hostname)) return "";
    }
    return parsed.toString().replace(/\/+$/, "");
  } catch {
    return "";
  }
}

const safeEnvBackend = sanitizeEnvBackend(envBackend);
const lanBackend = isBrowser && isPrivateOrLocalHost(currentHost)
  ? `${window.location.protocol}//${window.location.hostname}:4000`
  : "";
const defaultBackend = productionBackendByHost || lanBackend || currentOrigin || "http://localhost:4000";
const BACKEND_URL = (safeEnvBackend || defaultBackend).replace(/\/+$/, "");
const API_BASE = `${BACKEND_URL}/api`;
const SAME_ORIGIN_API_BASE = currentOrigin ? `${currentOrigin}/api` : API_BASE;

const axiosInstance = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

const RETRYABLE_STATUSES = new Set([500, 502, 503, 504]);
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function isRetryableGetError(error) {
  const status = Number(error?.response?.status || 0);
  // Network/CORS/interrupted request
  if (!status) return true;
  return RETRYABLE_STATUSES.has(status);
}

let lastClientErrorKey = "";
let lastClientErrorAt = 0;
let subscriptionRedirecting = false;

function shouldReportClientError(key) {
  const now = Date.now();
  if (key === lastClientErrorKey && now - lastClientErrorAt < 3000) return false;
  lastClientErrorKey = key;
  lastClientErrorAt = now;
  return true;
}

function shouldIgnoreCaughtApiError(error, message) {
  const status = Number(error?.response?.status || 0);
  const method = String(error?.config?.method || "get").toUpperCase();
  const path = String(error?.config?.url || "");
  const msg = String(message || "").toLowerCase();

  // Expected case: invoice detail route can be opened with a deleted/missing invoice id.
  if (
    status === 404 &&
    method === "GET" &&
    /^\/parasut\/invoices\/[^/]+$/.test(path) &&
    msg.includes("fatura bulunamad")
  ) {
    return true;
  }

  return false;
}

function reportCaughtApiError(error, message) {
  const token = localStorage.getItem("obihub_token");
  // `redact_telemetry: true` olan istekler (Entegrasyon Merkezi): rapora yanıt metni/yığın EKLENMEZ; yalnız yöntem + yol + durum gider.
  const redact = error?.config?.redact_telemetry === true;
  if (redact) message = "";
  if (!token) return;

  const path = String(error?.config?.url || "");
  if (!path || path.includes("/client-errors") || path.includes("/auth/super-") || path.startsWith("/super")) return;

  const status = error?.response?.status;
  const method = String(error?.config?.method || "get").toUpperCase();
  const page = window.location?.pathname || "";
  if (shouldIgnoreCaughtApiError(error, message)) return;
  const key = `${method}|${path}|${status || "0"}|${message || ""}`;
  if (!shouldReportClientError(key)) return;

  const payload = {
    source: "web",
    severity: !status ? "warning" : (status >= 500 ? "critical" : "error"),
    message: `[API] ${method} ${path} -> ${status || "NETWORK"} | ${message || "API error"}`,
    page,
    stack: redact ? "" : (error?.stack || ""),
    context: {
      status: status || null,
      method,
      path,
      is_network_error: !status,
    },
  };

  fetch(`${API_BASE}/client-errors`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  }).catch(() => {});
}

function normalizeApiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail.trim();

  if (Array.isArray(detail)) {
    const text = detail
      .map((item) => {
        if (!item) return null;
        if (typeof item === "string") return item;
        if (typeof item === "object") {
          const msg = item.msg || item.message || item.detail;
          if (typeof msg === "string" && msg.trim()) return msg.trim();
          return toSafeMessage(item, "");
        }
        return toSafeMessage(item, "");
      })
      .filter(Boolean)
      .join("\n");
    if (text) return text;
  }

  if (detail && typeof detail === "object") {
    return toSafeMessage(detail, "İstek doğrulama hatası");
  }

  return toSafeMessage(error?.message, "Bir hata oluştu");
}

// Request interceptor to add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    const url = String(config?.url || "");
    const isSuperRequest = url.includes("/auth/super-") || url.startsWith("/super");
    const token = localStorage.getItem(isSuperRequest ? "obihub_super_token" : "obihub_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    // Safari and some content blockers can fail cross-origin requests with a generic network error.
    // Retry once via same-origin /api when the configured backend is a different origin.
    if (
      isBrowser &&
      !error?.response &&
      error?.config &&
      !error.config.__sameOriginRetry &&
      currentOrigin &&
      !API_BASE.startsWith(SAME_ORIGIN_API_BASE)
    ) {
      try {
        return await axiosInstance.request({
          ...error.config,
          __sameOriginRetry: true,
          baseURL: SAME_ORIGIN_API_BASE,
        });
      } catch (retryError) {
        error = retryError;
      }
    }

    const message = toSafeMessage(normalizeApiErrorMessage(error));
    const url = String(error?.config?.url || "");
    const isSuperRequest = url.includes("/auth/super-") || url.startsWith("/super");

    // Also report caught API failures so Super Admin can see tenant-side errors.
    // `silent_statuses: [..]` — YALNIZ listelenen HTTP durumları (ör. beklenen 404/429) rapor edilmez; 5xx ve ağ hataları (durum yok) HER ZAMAN raporlanır.
    const failedStatus = Number(error?.response?.status || 0);
    const silentStatuses = error?.config?.silent_statuses;
    const isSilent = failedStatus > 0 && Array.isArray(silentStatuses) && silentStatuses.includes(failedStatus);
    if (!isSuperRequest && !isSilent) {
      reportCaughtApiError(error, message);
    }
    
    // Handle 401 errors (unauthorized)
    if (error.response?.status === 401) {
      if (isSuperRequest) {
        localStorage.removeItem("obihub_super_token");
        if (window.location.pathname !== "/super-giris") {
          window.location.href = "/super-giris";
        }
      } else {
        localStorage.removeItem("obihub_token");
        if (window.location.pathname !== "/giris") {
          window.location.href = "/giris";
        }
      }
    }

    // Handle subscription lock (expired/suspended/cancelled) gracefully.
    if (!isSuperRequest && error.response?.status === 403) {
      const detail = String(error?.response?.data?.detail || message || "");
      if (detail.toLowerCase().includes("abonelik erişimi pasif")) {
        localStorage.removeItem("obihub_token");
        if (!subscriptionRedirecting && window.location.pathname !== "/giris") {
          subscriptionRedirecting = true;
          window.location.href = "/giris";
        }
      }
    }
    
    if (error?.config?.expose_error_meta === true) {
      // Yalnız opt-in: sabit hata kodu (BÜYÜK_HARF) ve Retry-After saniyesi; ham gövde ASLA taşınmaz.
      const rawCode = error?.response?.data?.code;
      const code = typeof rawCode === "string" && /^[A-Z][A-Z0-9_]{2,63}$/.test(rawCode) ? rawCode : null;
      const ra = Number.parseInt(error?.response?.headers?.["retry-after"], 10);
      return Promise.reject({
        message: toSafeMessage(message),
        status: error.response?.status,
        code,
        retryAfter: Number.isFinite(ra) && ra > 0 && ra <= 3600 ? ra : null,
      });
    }

    return Promise.reject({ message: toSafeMessage(message), status: error.response?.status });
  }
);

export const api = {
  get: async (path, config) => {
    const attempts = Number(config?.retry_attempts || 3);
    const shouldRetry = config?.no_retry !== true;
    let lastError = null;
    for (let attempt = 1; attempt <= attempts; attempt += 1) {
      try {
        return await axiosInstance.get(path, config);
      } catch (err) {
        lastError = err;
        if (!shouldRetry || attempt >= attempts || !isRetryableGetError(err)) {
          throw err;
        }
        await sleep(200 * attempt);
      }
    }
    throw lastError;
  },
  post: (path, data, config) => axiosInstance.post(path, data, config),
  put: (path, data, config) => axiosInstance.put(path, data, config),
  patch: (path, data, config) => axiosInstance.patch(path, data, config),
  delete: (path, config) => axiosInstance.delete(path, config),
};

export default api;
